// MOXIE: Filtro de precio custom para la PLP — POC.
// El filtro nativo de Shopify (filter.type == 'price_range') no funciona en esta
// tienda B2B: su índice se calcula sobre el precio BASE del producto (siempre 0
// acá). Este archivo lo reemplaza filtrando 100% en el navegador sobre cards ya
// renderizadas (data-cpf-price, ya correcto por comprador/catálogo) — sin
// Storefront API, sin backend. Ver docs/bugs/BUG-plp-price-filter-catalogo-b2b.md
// y snippets/custom_price-filter.liquid (markup).
import { $4, debounce } from '@theme/utilities';

const CPF_MAX_AUTO_LOAD_PAGES = 15;

// MOXIE: la misma sección renderiza este filtro dos veces (drawer mobile en
// facets.liquid + sidebar desktop en sidebar-collection.liquid), ambas contra el
// mismo grid — antes cada <custom-price-range> tenía su propio estado y se
// pisaban entre sí en cada 'products:update' (auditoría mox-auditor, hallazgo
// crítico #1/#2). Un "motor" compartido por data-target centraliza el estado
// real (rango elegido, límites, autoLoadCount) y el único listener de
// 'products:update'; cada <custom-price-range> es solo una vista que refleja
// ese estado y le reenvía los cambios del usuario.
const engines = new Map();

function getEngine(target, sectionId) {
  let engine = engines.get(target);
  if (engine) return engine;

  const container = document.querySelector(target);
  engine = {
    container,
    views: new Set(),
    sectionId,
    rangeMax: 0,
    min: 0,
    max: 0,
    userNarrowedMax: false,
    autoLoadCount: 0,
  };

  engine.cards = () => (container ? Array.from(container.querySelectorAll('[data-cpf-price]')) : []);

  engine.render = () => {
    engine.views.forEach((view) => view.render());
  };

  engine.recalcBounds = (preserveNarrowedMax) => {
    const prices = engine.cards()
      .map((card) => parseFloat(card.dataset.cpfPrice))
      .filter((price) => !isNaN(price));
    if (!prices.length) return;

    engine.rangeMax = Math.ceil(Math.max(...prices));
    if (!preserveNarrowedMax || !engine.userNarrowedMax) {
      engine.max = engine.rangeMax;
    }
    engine.render();
  };

  engine.applyFilter = () => {
    let anyVisible = false;
    engine.cards().forEach((card) => {
      const price = parseFloat(card.dataset.cpfPrice);
      const inRange = !isNaN(price) && price >= engine.min && price <= engine.max;
      card.style.display = inRange ? '' : 'none';
      if (inRange) anyVisible = true;
    });

    engine.views.forEach((view) => {
      if (view.emptyEl) view.emptyEl.hidden = anyVisible;
    });

    if (!anyVisible) {
      engine.tryAutoLoadMore();
    } else {
      engine.views.forEach((view) => {
        if (view.loadingEl) view.loadingEl.hidden = true;
      });
    }
  };

  engine.tryAutoLoadMore = () => {
    const loadMoreLink = document.querySelector(`wrapp-hdt-lm-main-url[section-id="${engine.sectionId}"] a`);
    if (loadMoreLink && engine.autoLoadCount < CPF_MAX_AUTO_LOAD_PAGES) {
      engine.autoLoadCount++;
      engine.views.forEach((view) => {
        if (view.loadingEl) view.loadingEl.hidden = false;
      });
      loadMoreLink.click();
    } else {
      engine.views.forEach((view) => {
        if (view.loadingEl) view.loadingEl.hidden = true;
      });
    }
  };

  // MOXIE: disparado por LoadMore (assets/facets.min.js) tras cada "cargar
  // más" — así el filtro también cubre las cards recién inyectadas, sin tocar
  // ese archivo. Un solo listener por target (no uno por vista).
  engine.onProductsUpdate = () => {
    engine.recalcBounds(true);
    engine.applyFilter();
  };
  container?.addEventListener('products:update', engine.onProductsUpdate);

  engines.set(target, engine);
  return engine;
}

class CustomPriceRange extends HTMLElement {
  connectedCallback() {
    this.minRange = $4('[data-cpf-range-min]', this);
    this.maxRange = $4('[data-cpf-range-max]', this);
    this.minInput = $4('[data-cpf-min]', this);
    this.maxInput = $4('[data-cpf-max]', this);
    this.emptyEl = $4('[data-cpf-empty]', this);
    this.loadingEl = $4('[data-cpf-loading]', this);

    if (!this.minRange || !this.maxRange || !this.minInput || !this.maxInput) return;

    this.engine = getEngine(this.dataset.target, this.dataset.sectionId);
    this.engine.views.add(this);

    // MOXIE: este componente vive dentro del <form> nativo de facets.liquid
    // (hdt-facet-filters-form, render-on-change) — sin stopPropagation, cualquier
    // 'input'/'change' acá burbujea hasta ese form y dispara su auto-submit
    // nativo, recargando la página entera y perdiendo el estado del filtro custom.
    // Enter en los inputs numéricos también dispara el submit nativo del <form> —
    // se bloquea aparte (no burbujea como evento 'change').
    const stop = (event) => event.stopPropagation();
    const stopEnter = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    [this.minRange, this.maxRange, this.minInput, this.maxInput].forEach((el) => {
      el.addEventListener('input', stop);
      el.addEventListener('change', stop);
      el.addEventListener('keydown', stopEnter);
    });

    const commitRange = debounce(() => this.commitFromRange(), 300).bind(this);
    const commitNumber = debounce(() => this.commitFromNumber(), 350).bind(this);

    this.minRange.addEventListener('input', () => this.previewRangeMin());
    this.maxRange.addEventListener('input', () => this.previewRangeMax());
    this.minRange.addEventListener('change', commitRange);
    this.maxRange.addEventListener('change', commitRange);
    this.minInput.addEventListener('input', commitNumber);
    this.maxInput.addEventListener('input', commitNumber);

    if (this.engine.rangeMax) {
      this.render();
    } else {
      this.engine.recalcBounds(false);
    }
  }

  disconnectedCallback() {
    const engine = this.engine;
    if (!engine) return;
    engine.views.delete(this);
    if (engine.views.size === 0) {
      engine.container?.removeEventListener('products:update', engine.onProductsUpdate);
      engines.delete(this.dataset.target);
    }
  }

  // Vista local mientras se arrastra el slider — recién al soltar (evento
  // 'change', debounced) se confirma contra el motor compartido.
  previewRangeMin() {
    if (parseInt(this.minRange.value, 10) > parseInt(this.maxRange.value, 10)) {
      this.minRange.value = this.maxRange.value;
    }
    this.minInput.value = this.minRange.value;
    this.updateProgress();
  }

  previewRangeMax() {
    if (parseInt(this.maxRange.value, 10) < parseInt(this.minRange.value, 10)) {
      this.maxRange.value = this.minRange.value;
    }
    this.maxInput.value = this.maxRange.value;
    this.updateProgress();
  }

  commitFromRange() {
    const engine = this.engine;
    engine.min = parseInt(this.minRange.value, 10) || 0;
    engine.max = parseInt(this.maxRange.value, 10) || engine.rangeMax;
    engine.userNarrowedMax = engine.max < engine.rangeMax;
    engine.applyFilter();
    engine.render();
  }

  commitFromNumber() {
    const engine = this.engine;
    let min = parseInt(this.minInput.value, 10) || 0;
    let max = parseInt(this.maxInput.value, 10);
    if (isNaN(max)) max = engine.rangeMax;
    if (min > max) min = max;
    engine.min = min;
    engine.max = max;
    engine.userNarrowedMax = max < engine.rangeMax;
    engine.applyFilter();
    engine.render();
  }

  updateProgress() {
    const max = parseInt(this.maxRange.max, 10) || 1;
    const minPct = (parseInt(this.minRange.value, 10) / max) * 100;
    const maxPct = (parseInt(this.maxRange.value, 10) / max) * 100;
    this.style.setProperty('--min-progress', minPct + '%');
    this.style.setProperty('--max-progress', (100 - maxPct) + '%');
  }

  // Refleja el estado del motor compartido en el DOM de esta vista (drawer o
  // sidebar) para que ambas queden siempre en sync.
  render() {
    const engine = this.engine;
    [this.minRange, this.maxRange, this.minInput, this.maxInput].forEach((el) => {
      el.max = engine.rangeMax;
    });
    this.minRange.value = engine.min;
    this.maxRange.value = engine.max;
    this.minInput.value = engine.min;
    this.maxInput.value = engine.max;
    this.updateProgress();
  }
}

customElements.define('custom-price-range', CustomPriceRange);
